import { FC, useDeferredValue, useCallback } from 'react'
import { useWebsiteContext } from '../providers/website'
import { useWasmContext } from '../providers'
import type { Website } from '@app/types'
import { FeedTopSection } from './top'
import { DomainList } from './domain-list'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { useIssueFeedStyles } from './use-feed-issues-style'
import { useToast } from '@app/@/components/ui/use-toast'
// import { formatMockData } from '@app/data/mocks/format'

type LiveFeedProps = { small?: boolean; hideSync?: boolean; demo?: boolean }

// side panel that appears fixed on the right of current issues of domain being. This returns a list of pages with a list of issues per page.
export const IssueFeed: FC<LiveFeedProps> = ({ small, hideSync, demo }) => {
  const { t } = useTranslation('dashboard')
  const { feed } = useWasmContext()
  const { toast } = useToast()
  // todo: remove gql
  const { feedOpen, setIssueFeedContent, singlePageScan, forceUpdate } =
    useWebsiteContext()

  const { mainStyle, topStyles } = useIssueFeedStyles({ feedOpen, hideSync })

  const websites = useDeferredValue(feed?.get_data_keys() ?? [])

  // formatMockData(feed, "rhinobino.com", "tailwindcss.com")
  // formatMockData(feed, "elegantfisherman.com", "headlessui.com")

  const onScanEvent = useCallback(
    async (target: string) => {
      if (!feed) {
        // todo: demo toggle message saying just a demo!
        return
      }

      toast({
        // @ts-ignore
        title: t('dashboard:scan-progress'),
      })

      let webPage: Website | null = null
      const domain = new URL(target).hostname
      // the current item in the feed - wasm binds domain updates after
      const page = feed?.get_page(domain, target)

      try {
        webPage = await singlePageScan({ variables: { url: target } })
      } catch (e) {
        console.error(e)
      }

      // replace issue feed section with new value
      if (webPage && page) {
        const matchIssues = !webPage?.issue
        const cPageIssues = page?.issues?.length || 0

        if (matchIssues) {
          return
        }

        const lastCount = cPageIssues
        const nextCount = matchIssues
          ? cPageIssues
          : webPage?.issue?.length || 0

        if (lastCount === nextCount) {
          return
        }

        const issueDif = Math.abs(nextCount - lastCount)
        const issueMessage = nextCount > lastCount ? t('more') : t('less')

        toast({
          // @ts-ignore
          title: `${issueDif} ${issueMessage} ${
            issueDif === 1 ? t('issue') : t('issues')
          } found`,
        })
      }
    },
    [feed, singlePageScan, t, toast]
  )

  const clearData = useCallback(() => {
    feed?.clear_data()
    forceUpdate()
  }, [feed, forceUpdate])

  // SMALL LIST TO RENDER WITH BORDERS
  if (small) {
    // data loading view
    if (!websites.length) {
      return null
    }

    return (
      <div className='border rounded border-gray-800 dark:border-gray-600'>
        <FeedTopSection
          onClick={setIssueFeedContent}
          open={feedOpen}
          clearFeed={clearData}
          feedExist={websites.length}
          small={small}
          hideSync={hideSync}
        />
        <DomainList
          websites={websites}
          onScanEvent={onScanEvent}
          feed={feed}
          hideSync={hideSync}
          demo={demo}
        />
      </div>
    )
  }

  return (
    <>
      {feedOpen ? <style>{`body { overflow: hidden; }`}</style> : null}
      <div className={mainStyle} aria-live='polite'>
        <div className={topStyles}>
          <FeedTopSection
            open={feedOpen}
            feedExist={websites.length}
            hideSync={hideSync}
            onClick={setIssueFeedContent}
            clearFeed={clearData}
          />
          <DomainList
            websites={websites}
            onScanEvent={onScanEvent}
            feed={feed}
          />
        </div>
      </div>
    </>
  )
}
